import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, Sparkles, Wrench, AlertTriangle, Calendar, CheckCircle, Users, Search, Check, MapPin, Star, Clock } from 'lucide-react';
interface NuovoInterventoWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
type Step = 'type' | 'urgency' | 'searching' | 'result' | 'calendar' | 'success';
type InterventionType = 'pulizia' | 'manutenzione' | null;
type UrgencyType = 'urgente' | 'ordinario' | null;
export function NuovoInterventoWizard({
  isOpen,
  onClose
}: NuovoInterventoWizardProps) {
  const [step, setStep] = useState<Step>('type');
  const [type, setType] = useState<InterventionType>(null);
  const [urgency, setUrgency] = useState<UrgencyType>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('type');
      setType(null);
      setUrgency(null);
      setSelectedDate(null);
      setIsConfirming(false);
    }
  }, [isOpen]);
  const handleClose = () => {
    onClose();
  };
  const handleBack = () => {
    if (step === 'urgency') setStep('type');
    if (step === 'calendar') setStep('urgency');
    if (step === 'result') setStep('urgency');
  };
  const handleTypeSelect = (selectedType: InterventionType) => {
    setType(selectedType);
    setStep('urgency');
  };
  const handleUrgencySelect = (selectedUrgency: UrgencyType) => {
    setUrgency(selectedUrgency);
    if (selectedUrgency === 'urgente') {
      setStep('searching');
      // Simulate searching for operators
      setTimeout(() => {
        setStep('result');
      }, 2500);
    } else {
      setStep('calendar');
    }
  };
  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      setIsConfirming(false);
      setStep('success');
    }, 1500);
  };
  // Mock data for SkyScanner-style calendar
  const calendarDays = [{
    date: '15 Gen',
    dayName: 'Lun',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }, {
    date: '16 Gen',
    dayName: 'Mar',
    price: '€38',
    savings: '15%',
    hosts: 3,
    color: 'yellow'
  }, {
    date: '17 Gen',
    dayName: 'Mer',
    price: '€31',
    savings: '30%',
    hosts: 5,
    color: 'green'
  }, {
    date: '18 Gen',
    dayName: 'Gio',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }, {
    date: '19 Gen',
    dayName: 'Ven',
    price: '€38',
    savings: '15%',
    hosts: 2,
    color: 'yellow'
  }, {
    date: '20 Gen',
    dayName: 'Sab',
    price: '€45',
    savings: '0%',
    hosts: 0,
    color: 'gray'
  }, {
    date: '21 Gen',
    dayName: 'Dom',
    price: '€45',
    savings: '0%',
    hosts: 0,
    color: 'gray'
  }, {
    date: '22 Gen',
    dayName: 'Lun',
    price: '€29',
    savings: '35%',
    hosts: 6,
    color: 'green'
  }, {
    date: '23 Gen',
    dayName: 'Mar',
    price: '€38',
    savings: '15%',
    hosts: 2,
    color: 'yellow'
  }, {
    date: '24 Gen',
    dayName: 'Mer',
    price: '€45',
    savings: '0%',
    hosts: 1,
    color: 'gray'
  }];
  const getHeaderTitle = () => {
    switch (step) {
      case 'type':
        return 'Nuovo Intervento';
      case 'urgency':
        return 'Seleziona Urgenza';
      case 'searching':
        return 'Ricerca Operatori...';
      case 'result':
        return 'Operatore Trovato';
      case 'calendar':
        return 'Pianifica Intervento';
      case 'success':
        return 'Intervento Confermato!';
      default:
        return 'Nuovo Intervento';
    }
  };
  const getStepNumber = () => {
    switch (step) {
      case 'type':
        return 1;
      case 'urgency':
        return 2;
      case 'searching':
        return 3;
      case 'result':
        return 3;
      case 'calendar':
        return 3;
      case 'success':
        return 4;
      default:
        return 1;
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={handleClose} data-id="element-448">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-449">
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-450">
          <div className="flex items-center gap-3" data-id="element-451">
            {step !== 'type' && step !== 'searching' && step !== 'success' && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2" data-id="element-452">
                <ChevronLeft size={20} className="text-gray-500" data-id="element-453" />
              </button>}
            <div data-id="element-454">
              <h2 className="text-lg font-bold text-navy" data-id="element-455">
                {getHeaderTitle()}
              </h2>
              {step !== 'success' && step !== 'searching' && <p className="text-xs text-gray-500 mt-0.5" data-id="element-456">
                  Passo {getStepNumber()} di 3
                </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-457">
            <X size={20} className="text-gray-500" data-id="element-458" />
          </button>
        </div>

        {/* Progress Bar */}
        {step !== 'success' && step !== 'searching' && <div className="px-4 lg:px-6 pt-4" data-id="element-459">
            <div className="flex gap-2" data-id="element-460">
              {[1, 2, 3].map(s => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= getStepNumber() ? 'bg-coral' : 'bg-gray-200'}`} data-id="element-461" />)}
            </div>
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-462">
          {/* STEP 1: Type Selection */}
          {step === 'type' && <div className="space-y-4" data-id="element-463">
              <div data-id="element-464">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-465">
                  Che tipo di intervento ti serve?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-466">
                  Seleziona la categoria dell'intervento
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3" data-id="element-467">
                <button onClick={() => handleTypeSelect('pulizia')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-468">
                  <div className="flex items-start gap-4" data-id="element-469">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-470">
                      <Sparkles size={24} className="text-coral" data-id="element-471" />
                    </div>
                    <div className="flex-1" data-id="element-472">
                      <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-473">
                        Intervento di Pulizia
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-474">
                        Pulizie ordinarie, deep cleaning, o pulizie
                        post-soggiorno
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => handleTypeSelect('manutenzione')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-475">
                  <div className="flex items-start gap-4" data-id="element-476">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-477">
                      <Wrench size={24} className="text-amber-600" data-id="element-478" />
                    </div>
                    <div className="flex-1" data-id="element-479">
                      <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-480">
                        Intervento di Manutenzione
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-481">
                        Riparazioni, idraulica, elettricista o altri lavori
                        tecnici
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>}

          {/* STEP 2: Urgency Selection */}
          {step === 'urgency' && <div className="space-y-4" data-id="element-482">
              <div data-id="element-483">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-484">
                  Quanto è urgente?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-485">
                  Seleziona la priorità dell'intervento
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3" data-id="element-486">
                <button onClick={() => handleUrgencySelect('urgente')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-coral hover:bg-coral/5 transition-all group" data-id="element-487">
                  <div className="flex items-start gap-4" data-id="element-488">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-489">
                      <AlertTriangle size={24} className="text-red-600" data-id="element-490" />
                    </div>
                    <div className="flex-1" data-id="element-491">
                      <div className="flex items-center gap-2" data-id="element-492">
                        <span className="text-sm font-semibold text-navy group-hover:text-coral transition-colors" data-id="element-493">
                          Urgente (ASAP)
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-red-100 text-red-700 rounded-full" data-id="element-494">
                          Sovrapprezzo
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-495">
                        Cercheremo il primo operatore disponibile nelle
                        vicinanze. Ideale per emergenze.
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => handleUrgencySelect('ordinario')} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-coral hover:bg-coral/5 transition-all group" data-id="element-496">
                  <div className="flex items-start gap-4" data-id="element-497">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-498">
                      <Calendar size={24} className="text-coral" data-id="element-499" />
                    </div>
                    <div className="flex-1" data-id="element-500">
                      <div className="flex items-center gap-2" data-id="element-501">
                        <span className="text-sm font-semibold text-navy group-hover:text-coral transition-colors" data-id="element-502">
                          Ordinario (Pianificato)
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full" data-id="element-503">
                          Risparmia
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-504">
                        Scegli una data. Risparmia aggregandoti agli interventi
                        di altri Host nella tua zona.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>}

          {/* STEP 3A: Searching Animation */}
          {step === 'searching' && <div className="text-center py-12" data-id="element-505">
              <div className="relative w-24 h-24 mx-auto mb-6" data-id="element-506">
                <div className="absolute inset-0 border-4 border-gray-100 rounded-full" data-id="element-507"></div>
                <div className="absolute inset-0 border-4 border-teal-deep rounded-full border-t-transparent animate-spin" data-id="element-508"></div>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-509">
                  <Search size={32} className="text-teal-deep animate-pulse" data-id="element-510" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-511">
                Ricerca in corso...
              </h3>
              <p className="text-sm text-gray-500 max-w-[250px] mx-auto" data-id="element-512">
                Stiamo cercando il primo operatore disponibile per il tuo
                intervento urgente.
              </p>
            </div>}

          {/* STEP 3A: Urgent Result */}
          {step === 'result' && <div className="space-y-6" data-id="element-513">
              <div className="text-center" data-id="element-514">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-515">
                  <CheckCircle size={32} className="text-green-600" data-id="element-516" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-1" data-id="element-517">
                  Operatore Trovato!
                </h3>
                <p className="text-sm text-gray-500" data-id="element-518">
                  Un operatore è disponibile per intervenire a breve.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-deep rounded-xl p-4 shadow-sm relative overflow-hidden" data-id="element-519">
                <div className="absolute top-0 right-0 bg-teal-deep text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider" data-id="element-520">
                  Disponibile Ora
                </div>

                <div className="flex items-start gap-4 mt-2" data-id="element-521">
                  <img src="https://ui-avatars.com/api/?name=Marco+R&background=0D9488&color=fff&size=64" alt="Operatore" className="w-16 h-16 rounded-full border-2 border-gray-100" data-id="element-522" />
                  <div className="flex-1" data-id="element-523">
                    <h4 className="text-base font-bold text-navy" data-id="element-524">
                      Marco Rossi
                    </h4>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-525">
                      <Star size={12} className="text-yellow-400 fill-current" data-id="element-526" />
                      <span className="font-medium text-gray-700" data-id="element-527">4.9</span>
                      <span data-id="element-528">(124 recensioni)</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-529">
                      <MapPin size={12} data-id="element-530" />
                      <span data-id="element-531">A 2.5 km da te</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-532">
                      <Clock size={12} data-id="element-533" />
                      <span className="font-medium text-teal-deep" data-id="element-534">
                        Arrivo stimato: 45 min
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center" data-id="element-535">
                  <div data-id="element-536">
                    <div className="text-xs text-gray-500" data-id="element-537">Costo stimato</div>
                    <div className="text-lg font-bold text-navy" data-id="element-538">€65,00</div>
                  </div>
                  <div className="text-right" data-id="element-539">
                    <div className="text-xs text-gray-500" data-id="element-540">
                      Incluso sovrapprezzo urgenza
                    </div>
                  </div>
                </div>
              </div>

              {/* Urgent Result - Accept button */}
              <button onClick={handleConfirm} disabled={isConfirming} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${!isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-541">
                {isConfirming ? <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-542" />
                    Conferma in corso...
                  </> : <>
                    <CheckCircle size={18} data-id="element-543" />
                    Accetta Intervento
                  </>}
              </button>
            </div>}

          {/* STEP 3B: SkyScanner Calendar */}
          {step === 'calendar' && <div className="space-y-4" data-id="element-544">
              <div data-id="element-545">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-546">
                  Scegli la data e risparmia
                </h3>
                <p className="text-xs text-gray-500" data-id="element-547">
                  Aggregati agli interventi di altri Host nella tua zona per
                  ottenere sconti.
                </p>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-[10px] font-medium text-gray-500 bg-gray-50 p-2 rounded-lg" data-id="element-548">
                <div className="flex items-center gap-1.5" data-id="element-549">
                  <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300" data-id="element-550"></div>
                  <span data-id="element-551">Alto risparmio</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-552">
                  <div className="w-3 h-3 rounded-full bg-yellow-100 border border-yellow-300" data-id="element-553"></div>
                  <span data-id="element-554">Medio risparmio</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-555">
                  <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-300" data-id="element-556"></div>
                  <span data-id="element-557">Prezzo base</span>
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-558">
                {calendarDays.map((day, idx) => {
              const isSelected = selectedDate === day.date;
              let bgColor = 'bg-white';
              let borderColor = 'border-gray-200';
              let textColor = 'text-navy';
              let savingsColor = 'text-gray-400';
              if (isSelected) {
                bgColor = 'bg-teal-50';
                borderColor = 'border-teal-deep';
              } else if (day.color === 'green') {
                bgColor = 'bg-green-50/50';
                borderColor = 'border-green-200';
                savingsColor = 'text-green-600';
              } else if (day.color === 'yellow') {
                bgColor = 'bg-yellow-50/50';
                borderColor = 'border-yellow-200';
                savingsColor = 'text-yellow-600';
              }
              return <button key={idx} onClick={() => setSelectedDate(day.date)} className={`relative flex flex-col p-3 rounded-xl border-2 transition-all text-left hover:border-teal-deep ${bgColor} ${borderColor}`} data-id="element-559">
                      {isSelected && <div className="absolute -top-2 -right-2 w-5 h-5 bg-teal-deep rounded-full flex items-center justify-center border-2 border-white" data-id="element-560">
                          <Check size={12} className="text-white" data-id="element-561" />
                        </div>}

                      <div className="flex justify-between items-start mb-2" data-id="element-562">
                        <div data-id="element-563">
                          <span className="text-[10px] font-medium text-gray-500 uppercase block" data-id="element-564">
                            {day.dayName}
                          </span>
                          <span className={`text-sm font-bold ${textColor}`} data-id="element-565">
                            {day.date}
                          </span>
                        </div>
                        <span className={`text-sm font-bold ${textColor}`} data-id="element-566">
                          {day.price}
                        </span>
                      </div>

                      <div className="mt-auto pt-2 border-t border-gray-100/50" data-id="element-567">
                        {day.hosts > 0 ? <div className="flex flex-col gap-0.5" data-id="element-568">
                            <span className={`text-[10px] font-bold ${savingsColor}`} data-id="element-569">
                              -{day.savings} di sconto
                            </span>
                            <span className="text-[9px] text-gray-500 flex items-center gap-1" data-id="element-570">
                              <Users size={8} data-id="element-571" /> {day.hosts} Host aggregati
                            </span>
                          </div> : <div className="flex flex-col gap-0.5" data-id="element-572">
                            <span className="text-[10px] font-medium text-gray-400" data-id="element-573">
                              Prezzo standard
                            </span>
                            <span className="text-[9px] text-gray-400" data-id="element-574">
                              Nessun Host aggregato
                            </span>
                          </div>}
                      </div>
                    </button>;
            })}
              </div>

              {/* Calendar - Confirm Date button */}
              <button onClick={handleConfirm} disabled={!selectedDate || isConfirming} className={`w-full mt-4 px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${selectedDate && !isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-575">
                {isConfirming ? <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-576" />
                    Conferma in corso...
                  </> : <>
                    <CheckCircle size={18} data-id="element-577" />
                    Conferma Data
                  </>}
              </button>
            </div>}

          {/* STEP 4: Success */}
          {step === 'success' && <div className="text-center py-6" data-id="element-578">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-579">
                <CheckCircle size={32} className="text-green-600" data-id="element-580" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-581">
                Intervento Confermato!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-582">
                Il tuo intervento è stato programmato con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-583">
                <div className="flex justify-between text-sm" data-id="element-584">
                  <span className="text-gray-600" data-id="element-585">Tipo</span>
                  <span className="font-medium text-navy capitalize" data-id="element-586">
                    {type}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-587">
                  <span className="text-gray-600" data-id="element-588">Urgenza</span>
                  <span className="font-medium text-navy capitalize" data-id="element-589">
                    {urgency}
                  </span>
                </div>
                {urgency === 'ordinario' && selectedDate && <div className="flex justify-between text-sm" data-id="element-590">
                    <span className="text-gray-600" data-id="element-591">Data</span>
                    <span className="font-medium text-navy" data-id="element-592">
                      {selectedDate}
                    </span>
                  </div>}
                {urgency === 'urgente' && <div className="flex justify-between text-sm" data-id="element-593">
                    <span className="text-gray-600" data-id="element-594">Operatore</span>
                    <span className="font-medium text-navy" data-id="element-595">Marco Rossi</span>
                  </div>}
              </div>

              {/* Success - Close button */}
              <button onClick={handleClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-medium hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-596">
                Chiudi
              </button>
            </div>}
        </div>
      </div>
    </div>;
}